import React, { useCallback, useRef } from 'react';
import useScrolledShadow from 'hooks/useScrolledShadow';

/**
 * Composant SubNavbar réutilisable pour maintenir la cohérence des bordures et espacements
 * @param {React.ReactNode} children - Contenu à afficher dans la subnavbar
 * @param {boolean} sticky - Si true, la subnavbar reste fixe en haut lors du scroll
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} containedLayout - Désactive le scroll horizontal global ; le scroll reste aux enfants
 * @param {boolean} elevated - The page's own scroller, right under the bar, is scrolled: the bar casts a shadow
 */
const SubNavbar = ({ children, sticky = true, className = '', containedLayout = false, elevated = false }) => {
  const scrollContainerRef = useRef(null);
  const { ref: watchPageScroll, isScrolled: isPageScrolled } = useScrolledShadow();

  // A sticky bar stays put while PageForm's <main> scrolls under it. Shadows
  // from tab up only: below, the bar is neither sticky nor stacked above the
  // content, which would paint over its shadow.
  const watchMainOf = useCallback(
    (node) => watchPageScroll(sticky ? node?.closest('main') ?? null : null),
    [watchPageScroll, sticky],
  );

  const handleWheel = (e) => {
    if (containedLayout) return;

    const container = scrollContainerRef.current;
    if (container && e.deltaY !== 0) {
      // Vérifier si le contenu dépasse la largeur visible
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (hasHorizontalScroll) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    }
  };

  return (
    <div
      ref={watchMainOf}
      className={`w-full bg-white border-b border-gray-100 transition-shadow duration-150 ${sticky ? 'tab:sticky tab:top-0 z-20' : ''} ${elevated || isPageScrolled ? 'tab:shadow-[0_4px_6px_-4px_rgba(17,24,39,0.12)]' : ''} ${className}`}
    >
      <div
        ref={scrollContainerRef}
        className={`px-3 tab:px-4 lg:px-8 min-h-[72px] flex items-center ${
          containedLayout
            ? 'overflow-x-hidden overflow-y-hidden'
            : 'overflow-x-auto overflow-y-hidden thin-scrollbar'
        }`}
        style={{ WebkitOverflowScrolling: 'touch' }}
        onWheel={handleWheel}
      >
        <div
          className={
            containedLayout
              ? 'flex items-center gap-3 min-w-0 w-full'
              : 'flex items-center gap-3 whitespace-nowrap min-w-max w-full justify-between'
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default SubNavbar;

